/**
 * ZAGX v2 — Utility and Formatting Functions
 * Extracted from index.html lines 1125-1148
 * Shared state reference pattern to avoid circular dependencies
 */

// Shared state reference — set by state.js on init
let _S = null;

export function _setStateRef(s) {
  _S = s;
}

/**
 * XSS escape: safely converts text to HTML by escaping special chars
 */
export const esc = s => {
  if (s == null) return '';
  const d = document.createElement('div');
  d.textContent = String(s);
  return d.innerHTML;
};

/**
 * Scheme allow-list for external links from untrusted sources (Mongo feeds, AI, news).
 * Blocks javascript:/data:/etc. Returns '' for anything but http(s)/mailto so href is inert.
 */
export const safeUrl = u => {
  if (!u) return '';
  const s = String(u).trim();
  return /^(https?:|mailto:)/i.test(s) ? s : '';
};

/**
 * Unique ID generator.
 *
 * The previous implementation was `Date.now().toString(36) + Math.random().toString(36).slice(2,7)`
 * — a millisecond stamp plus FIVE base-36 characters, i.e. 36^5 = 60,466,176 values. That is a
 * birthday problem, not a guarantee: 100 ids inside one millisecond collide 0.0065% of the time
 * (measured over 200,000 trials), 1,000 collide 0.8%, 5,000 collide 18.7%. It took the deploy gate
 * down on 2026-08-14 — `utils.test.js > generates unique IDs` drew 99 of 100 — and that test was
 * itself a coin flip that had simply always landed.
 *
 * An id collision here is a data-loss class, not a cosmetic one: `_deletedIds` tombstones by id
 * (delete one row, its twin dies on the next merge), every `findIndex(x => x.id === id)` takes the
 * first match and orphans the second, the cloud merge de-duplicates by id, and `_divId` / `lotId`
 * back-links resolve to the wrong record.
 *
 * The layout below makes a same-process collision IMPOSSIBLE rather than improbable, in fixed-width
 * fields so that one field can never bleed into the next:
 *
 *   9 chars  millisecond, base36, zero-padded   (fits until the year 5138)
 *   2 chars  per-millisecond sequence, base36   (1,296 ids per ms; then it borrows the next ms)
 *   6 chars  CSPRNG entropy, base36             (36^6 = 2.18e9, for OTHER tabs and devices)
 *
 * Same process: (ms, seq) is unique by construction — the sequence is what the old id lacked.
 * Different process: the 6-char tail carries it, and two devices must also collide on ms AND seq.
 *
 * Ids stay lexicographically time-ordered, which the old shape gave by accident and nothing in
 * `src/` currently depends on (no id is parsed, sliced or sorted anywhere — swept 2026-08-14).
 */
const _UID_MS_LEN = 9;
const _UID_SEQ_LEN = 2;
const _UID_SEQ_MAX = Math.pow(36, _UID_SEQ_LEN) - 1; // 1295
const _UID_RAND_LEN = 6;

let _uidMs = 0;
let _uidSeq = 0;

/** base36 noise from the CSPRNG, with a Math.random fallback for hosts that lack one. */
function _uidRand(n) {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  let s = '';
  if (c && typeof c.getRandomValues === 'function') {
    const a = new Uint32Array(n);
    c.getRandomValues(a);
    for (let i = 0; i < n; i++) s += (a[i] % 36).toString(36);
    return s;
  }
  while (s.length < n) s += Math.random().toString(36).slice(2);
  return s.slice(0, n);
}

export const uid = () => {
  const now = Date.now();
  if (now > _uidMs) {
    _uidMs = now;
    _uidSeq = 0;
  } else if (_uidSeq >= _UID_SEQ_MAX) {
    // The sequence for this millisecond is spent. Borrow the next one rather than wrap — the
    // pair must keep increasing, and wall-clock catches up on its own.
    _uidMs += 1;
    _uidSeq = 0;
  } else {
    _uidSeq += 1;
  }
  return (
    _uidMs.toString(36).padStart(_UID_MS_LEN, '0') +
    _uidSeq.toString(36).padStart(_UID_SEQ_LEN, '0') +
    _uidRand(_UID_RAND_LEN)
  );
};

/**
 * Number formatter with specified decimal places
 */
export const f = (n, d = 2) =>
  n == null || isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });

/**
 * USD formatter
 */
export const fU = n =>
  n == null || isNaN(n) ? '—' : '$' + f(n);

/**
 * TRY formatter
 */
export const fT = n =>
  n == null || isNaN(n) ? '—' : '₺' + f(n);

/**
 * Percent formatter
 */
export const fP = n =>
  n == null || isNaN(n) ? '—' : (n >= 0 ? '+' : '') + f(n) + '%';

/**
 * Positive/Negative/Neutral class selector
 */
export const pc = n =>
  isNaN(n) || n == null ? 'neu' : n > 0 ? 'pos' : n < 0 ? 'neg' : 'neu';

/**
 * Badge class selector
 */
export const bc = t => 'b-' + t.toLowerCase();

/**
 * ISO timestamp generator
 */
export const now = () => new Date().toISOString();

/**
 * Format value by currency
 */
export const fByCur = (cur, v) => {
  if (v == null || isNaN(v)) return '—';
  if (cur === 'TRY') return fT(v);
  if (cur === 'EUR') return '€' + f(v);
  return fU(v);
};

/**
 * Valid currency list
 */
export const VALID_CURRENCIES = ['USD', 'TRY', 'EUR'];

/**
 * USD/TRY and EUR/TRY, or null when unusable.
 *
 * `S.fxRate || 1` asserts 1 TRY = 1 USD — a ~40x error rendered in the same style as a
 * real figure. These accessors and the two converters below return null instead, and the
 * fU/fT formatters already render null as an em dash, so a missing rate reads as missing.
 * (audit 2026-08-01)
 *
 * These two existed since that audit and, until 2026-08-03, had ZERO callers: all 41 sites
 * still read `S.fxRate || 1` directly, and the eslint rule banning it carried an `ignores`
 * list standing in for the backlog. The four LLM prompt builders now use them — that is the
 * subset where a fabricated dollar does not merely render wrong, it becomes advice the owner
 * is asked to act on.
 */
export function fxUSDTRY() { return typeof _S?.fxRate === 'number' && _S.fxRate > 0 ? _S.fxRate : null; }
export function fxEURTRY() { return typeof _S?.fxRateEUR === 'number' && _S.fxRateEUR > 0 ? _S.fxRateEUR : null; }

/** USD amount -> TRY, or null when there is no usable rate. */
export function usdToTRY(v) {
  const r = fxUSDTRY();
  return (r != null && v != null && !isNaN(v)) ? v * r : null;
}

/** USD amount -> EUR, or null. Uses the direct rate first, then the TRY cross. */
export function usdToEUR(v) {
  if (v == null || isNaN(v)) return null;
  if (_S?.fxEURUSD > 0) return v * _S.fxEURUSD;
  const t = fxUSDTRY(), e = fxEURTRY();
  return (t != null && e != null) ? (v * t) / e : null;
}

/**
 * Convert to USD, or return null when it cannot be done.
 *
 * toUSD() returns 0 on a missing rate, which is correct for a display total that should
 * simply omit an unconvertible line, but catastrophic anywhere the result is PERSISTED or
 * used as a denominator: a realized P&L written as 0 is wrong forever, and a reserve
 * coverage ratio computed with `fxRate || 1` treats 1 TRY as 1 USD.
 *
 * Use this at every site where a wrong number is worse than a missing one, and handle the
 * null explicitly. (audit 2026-08-01)
 */
export function toUSDStrict(val, cur) {
  if (val == null || isNaN(val)) return null;
  if (!cur || cur === 'USD') return val;
  if (!VALID_CURRENCIES.includes(cur)) return null;
  if (cur === 'TRY') {
    return (_S?.fxRate > 0) ? val / _S.fxRate : null;
  }
  if (cur === 'EUR') {
    if (_S?.fxEURUSD > 0) return val / _S.fxEURUSD;
    if (_S?.fxRateEUR > 0 && _S?.fxRate > 0) return (val * _S.fxRateEUR) / _S.fxRate;
    return null;
  }
  return null;
}

/**
 * Convert any currency to USD using exchange rates from state
 */
/**
 * THE CURRENCY THE MONEY ACTUALLY MOVES IN when a holding is bought or sold.
 *
 * For every ordinary holding it is the holding's own currency: 10 shares at $50 costs $500.
 * A **Cash holding is the exception and the exception is total** — `qty` is the AMOUNT held
 * (927.4652 USD) and `price`/`avgCost` is the FX RATE (45.28 lira per dollar), so `qty * price`
 * is denominated in the currency HANDED OVER, which is lira.
 *
 * `saveAddLot` learned this on 2026-08-07, before the row was ever written: debiting
 * `h.currency` would have taken TRY 2,439 out of a USD 400 balance as if it were dollars. Both
 * EXIT paths never learned it. Selling the USD cash holding would have credited **TRY 44,342
 * into `S.cash.USD`** — a ~47x inflation of the dollar balance — and booked `pnlUSD` by
 * converting a lira P&L as though it were already dollars. Two holdings are in that shape
 * (USD and EUR), one on each exit path, TRY 90,617 between them. (SPX-ACCT-01, 2026-08-14)
 *
 * 'TRY' is not a guess: every cash holding in this book quotes its rate as lira per unit
 * (usdtry, eurtry), the convention monthly.js and hVal already rely on.
 */
/**
 * A value that is about to be interpolated into an LLM prompt, reduced so that it cannot
 * RESTRUCTURE the message it is going into.
 *
 * Every prompt in this app is line- and pipe-structured — `id | source | age | title`, one row
 * per line, inside `=== SECTION ===` headers. That is a readable format and a forgeable one: a
 * field containing a newline creates a row that was never in the data, and a field containing
 * `===` opens a section the builder did not write. The model has no way to tell a forged row
 * from a real one, because by the time it sees the string there is no structure left, only text.
 *
 * The exposure is not theoretical and it is not only about him. `ai-impact.js` and the copilot's
 * news fallback interpolate **headlines fetched from the internet**; `signals.js` interpolates
 * analyst notes out of a MongoDB feed; `ai-categorize.js` interpolates `tx.note` and `tx.reason`,
 * and "Other" is deliberately free text. `ai-actions.js` interpolated holdings into the SYSTEM
 * role until 2026-08-14, where state can arrive from an import or a cloud merge.
 *
 * What this does NOT do: make a prompt injection-proof. A model can still be persuaded by text
 * that is structurally innocent. What it removes is the ability to forge the frame — and it
 * keeps the words, because the model needs the actual headline to be useful.
 *
 * (audit 2026-08-14)
 */
export function promptSafe(v, max = 160) {
  return String(v == null ? '' : v)
    .replace(/\|/g, '/')        // no forged columns
    .replace(/={3,}/g, '==')    // no forged section headers
    .replace(/`/g, "'")         // no code fences
    // AND NO FORGED ROWS. This one line is the whole line-break defence: JavaScript's `\s`
    // includes \r, \n, U+2028 and U+2029, so collapsing whitespace collapses every separator
    // there is. A separate `[\r\n\u2028\u2029]` replace was written above it first and
    // MUTATION TESTING PROVED IT DEAD — breaking it killed nothing, because this line had
    // already done the work. A line no mutation can reach is a line doing no work.
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

/**
 * The largest amount this book will accept in one movement. A cap is not paranoia: it is the
 * difference between a typo and a corrupted balance, and it has to be a NUMBER somewhere.
 */
export const AMOUNT_CAP = 1e12;

/**
 * A number that can be money. Finite, positive, and inside the cap.
 *
 * `saveCTx` has checked exactly this since the Cash modal was written — `!am || am <= 0 ||
 * !isFinite(am)` and `am > 1e12`. The AI-chat door checked only `!amount || amount <= 0`, and
 * **`Infinity` passes that**: measured 2026-08-14, a deposit of `Infinity` succeeded, reported
 * "Deposited ₺∞" and left `S.cash.TRY = Infinity`. `1e309` — an ordinary-looking JSON number —
 * parses to Infinity, so an LLM does not have to be adversarial to produce it. `persistence.js`
 * repairs NaN on load and lets Infinity through, because Infinity is a number and is not NaN.
 *
 * One rule, both doors. (2026-08-14)
 */
export function isUsableAmount(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 && n <= AMOUNT_CAP;
}

export function settlementCurrency(h) {
  return (h && h.type === 'Cash') ? 'TRY' : ((h && h.currency) || 'USD');
}

export function toUSD(val, cur) {
  if (val == null || isNaN(val)) return 0;
  // ZERO NEEDS NO RATE. Zero lira is zero dollars whatever the rate is, so refusing to answer
  // is not integrity here — it is 18 warnings on every boot from computeTotalWealthUSD
  // converting an empty S.cash before the state has hydrated, which is exactly the noise a
  // real missing rate has to be visible against. A NON-zero amount with no rate still warns
  // and still returns 0, because that one genuinely cannot be answered. (audit 2026-08-03)
  if (val === 0) return 0;
  if (!cur || cur === 'USD') return val;
  if (!VALID_CURRENCIES.includes(cur)) {
    console.warn(
      '[ZAGX] toUSD: unknown currency "' + cur + '", returning 0'
    );
    return 0;
  }
  if (cur === 'TRY') {
    if (!_S?.fxRate || _S.fxRate <= 0) {
      console.warn(
        '[ZAGX] toUSD: fxRate missing/invalid for TRY conversion'
      );
      return 0;
    }
    return val / _S.fxRate;
  }
  if (cur === 'EUR') {
    // CE-06 Fix: Use direct EUR/USD rate when available, fallback to cross rate
    if (_S?.fxEURUSD && _S.fxEURUSD > 0) return val / _S.fxEURUSD;
    if (!_S?.fxRateEUR || !_S?.fxRate || _S.fxRate <= 0) {
      console.warn(
        '[ZAGX] toUSD: fxRate/fxRateEUR missing for EUR conversion'
      );
      return 0;
    }
    return (val * _S.fxRateEUR) / _S.fxRate;
  }
  return 0;
}

/**
 * Historical USD conversion (audit 2026-07-06 #8).
 * Month-based paths (monthly plan, FX-adjusted dashboards) already convert at
 * the month's reconciled rate; the fallback/lot paths converted historical
 * amounts at TODAY's rate, which systematically flatters USD returns while
 * TRY depreciates. This helper resolves the rate from S.monthlyRates[YYYY-MM]
 * (usdtry / eurtry) for the event's month and only falls back to the live
 * rate when that month has no reconciled rate.
 */
export function toUSDAt(val, cur, dateISO) {
  if (val == null || isNaN(val)) return 0;
  if (!cur || cur === 'USD') return val;
  const key = typeof dateISO === 'string' ? dateISO.slice(0, 7) : '';
  const mr = key && _S?.monthlyRates ? _S.monthlyRates[key] : null;
  if (mr) {
    if (cur === 'TRY' && mr.usdtry > 0) return val / mr.usdtry;
    if (cur === 'EUR' && mr.eurtry > 0 && mr.usdtry > 0) return (val * mr.eurtry) / mr.usdtry;
  }
  return toUSD(val, cur); // month unknown → live rate (previous behavior)
}

/**
 * Canonical USD value of a realized-exit record: stored pnlUSD when present
 * (captured at exit time), else historical conversion at the exit month.
 */
export function realizedUSD(r) {
  if (!r) return 0;
  return r.pnlUSD != null ? r.pnlUSD : toUSDAt(r.pnl || 0, r.currency || 'USD', r.date);
}

/**
 * A published date, as ISO, or null.
 *
 * Signal imports store `sourceDate` verbatim from the source page's meta tag, so the book holds
 * two formats at once: 150 rows of '2026-07-30' and 18 of 'May 2, 2026'. Sorting those as
 * STRINGS ranks every textual month above every ISO date ('M' > '2'), which on 2026-08-03 made
 * the integrity check report a four-day-old feed as three months stale.
 *
 * The rule is not "be lenient". It is: one function decides, and it requires an EXPLICIT YEAR.
 * `Date.parse('May 4')` cheerfully returns May 4 of the current year — an inferred year is a
 * fabricated fact, and this is a date the app sorts by. A year that is not in the string is a
 * date this returns null for.
 */
export function isoDate(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return isFinite(raw) && raw > 0 ? new Date(raw).toISOString().slice(0, 10) : null;
  }
  const str = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  if (!/\b\d{4}\b/.test(str)) return null;          // no explicit year -> not a date we will guess
  const t = Date.parse(str);
  return isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
}

/**
 * Canonical USD value of a dividend record: stored netUSD when present (captured at record
 * time), else live conversion.
 *
 * `netUSD` is written at entry precisely so a payment received at 38 TRY/USD does not restate
 * itself every time the rate moves — the sibling of realizedUSD above. Eleven places read
 * dividends; eight honoured the stored value and three did not:
 *
 *   goals.js       TOTAL EARNED — the dividend leg
 *   portfolio.js   the "Total Dividends" cell, and the hero's Dividends / Employer-match chips
 *
 * All three did `toUSD(d.net, d.currency)` at TODAY's rate, so the same historical payment
 * showed one number on the Money page and a different one on Goals, and both moved with FX.
 * A stored figure that only some readers respect is worse than no stored figure: it looks
 * authoritative and disagrees with itself. (audit 2026-08-03)
 */
export function dividendUSD(d) {
  if (!d) return 0;
  return d.netUSD != null ? d.netUSD : toUSD(d.net || 0, d.currency || 'USD');
}

/**
 * A dividend row that is PASSIVE income: paid by the instrument, not by the employer.
 * `kind: 'match'` marks the SAP plan's employer contribution (reclass 2026-07-21): income, but
 * it dies with employment, so it never enters yield, run-rate or FI passive income. The rule
 * lived as `d.kind === 'match'` in eight places and the Portfolio yield widget was not one of
 * them: eleven monthly match rows made a ~$32k sleeve read a 12.8 % forward yield.
 */
export function isPassiveDividend(d) {
  return !!d && d.kind !== 'match';
}

/**
 * True when a realized-exit record is Time-Deposit interest, not a directional
 * trade. New TD closes carry kind:'interest'; legacy records are detected by
 * reason so history is never rewritten. Used to keep interest out of trading
 * win-rate / trading-realized while still counting it in total earnings.
 */
export function isInterestRealized(r) {
  if (!r) return false;
  return r.kind === 'interest' || r.reason === 'Daily TD closed' || r.reason === 'Matured';
}

/**
 * WHERE A REALIZED ROW CAME FROM — 'trade' | 'portfolio' | 'interest'.
 *
 * ⚠ `S.realized` IS THE WHOLE BOOK'S LEDGER, NOT A LIST OF TRADES. Five writers push into it:
 *
 *   trades.js execExit          tradeId set                    -> trade
 *   ai-actions.js AI exit       tradeId set                    -> trade
 *   portfolio.js exitH (lots)   reason 'Exit Holding'          -> portfolio
 *   portfolio.js exitH (avg)    reason 'Exit Holding'          -> portfolio
 *   ai-actions.js holding sell  reason 'AI Chat holding exit'  -> portfolio
 *   portfolio.js TD close/mature  kind 'interest'              -> interest
 *
 * He found this on 2026-08-16: *"hvz otj vs bunlar trade değil ki, onlar portfolio'dan çıktı."*
 * Measured on `backup/zagx-2026-08-14.json`, 21 rows — **13 portfolio exits, 1 deposit, 7
 * trades** — so the Trades page headline "win rate 90 %" was mostly rebalancing. Trades alone:
 * 100 % over 7. Realized $3.961,60 shown against $3.023,37 from trades.
 *
 * ⚠ **A MISSING `tradeId` DOES NOT MEAN "NOT A TRADE".** Four April rows — EREGL *TP1 Hit*,
 * AKBNK / GWIND / XAU *Manual* — predate the link and are genuine trades. So the discriminator
 * is what the PORTFOLIO writers stamp, not the absence of an id, and an unrecognised row falls
 * to 'trade' rather than being quietly dropped from the count. Rule #32.
 */
/**
 * ONE SPELLING OF A TICKER — `.IS` off, upper-cased, trimmed.
 *
 * The signal feed writes BIST symbols as `ASELS.IS`; the trade book and the holdings write
 * `ASELS`. Every join between them has to reconcile that, and by 2026-08-16 the reconciliation
 * was written **SIX TIMES** by hand, all in `signals.js`: lines 635, 1148, 1149, 1204, 1207 and
 * 1348 — the last of which had already named it `const norm = t => …` locally.
 *
 * Found while measuring Trades part 2, and it is the exact shape the quantity registry exists to
 * stop (theme #1, twenty-six instances). Row `ticker.normalised` in
 * `tests/quantity-registry.js` fails the build on a seventh copy.
 *
 * ⚠ **`.IS` IS STRIPPED ANYWHERE IN THE STRING BY THE OLD COPIES** — `String.replace` with a
 * plain string replaces the FIRST occurrence, so a hypothetical `X.ISY` would be mangled. This
 * anchors to the END, which is what every caller meant. Behaviour on every real ticker is
 * identical; the difference is that this one cannot be surprised.
 */
export function normTicker(t) {
  return String(t == null ? '' : t).trim().toUpperCase().replace(/\.IS$/, '');
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * ONE ANALYST IS ONE ANALYST — undoing UTF-8 text that was decoded as GBK.
 *
 * Measured 2026-08-16 over **all 10,101 strings in the live book**. Six mis-decoded characters,
 * **129 occurrences**, and they sit in exactly two fields: `signals[].sourceName` and
 * `signals[].sourceTitle`. Nothing else in state is touched.
 *
 *     U+964C x66 -> İ      U+8266 x11 -> Ş
 *     U+8C0B x30 -> ı      U+813A x 3 -> Ü
 *     U+811F x16 -> Ç      U+83BD x 3 -> ç
 *
 * ⚠ THE MECHANISM IS EXACT, NOT A GUESS. `ı` is U+0131, which is **C4 B1** in UTF-8; read those
 * two bytes as GBK/CP936 and you get **U+8C0B**. All six round-trip perfectly, and the test
 * re-derives this whole table with `TextDecoder('gbk')` rather than trusting the literal below.
 *
 * ⚠ IT IS NOT THIS APP'S BUG, AND IT IS STILL THIS APP'S PROBLEM. `mapMarketStateDoc` copies
 * `metadata.channel_name` and `metadata.title` verbatim, so a constant mis-decode here would have
 * corrupted every row. It corrupts **4 videos of 25, all-or-nothing within a video** (zero
 * partially-affected videos) — per-document, written that way upstream in the pipeline that fills
 * Mongo. The upstream fix is owed there; this makes the app read correctly either way, which is
 * also why it lives at `save()` and is replayable: a corrupt document can arrive again tomorrow.
 *
 * Why it matters beyond looking wrong: the two spellings of one channel name are **two different
 * analysts to every `Set` in the codebase** — `_sigConsensus`'s distinct-analyst count,
 * `signalAnalystStats`' grouping, and `signalStance.sourceSpellings`, which reports 2 for both of
 * his tickers where the answer is 1. Same family as `normTicker` above: one quantity, two
 * spellings.
 *
 * ⚠ SCOPED TO TURKISH ON PURPOSE. The table covers the Turkish alphabet plus the accented Latin
 * letters that appear in Turkish loanwords. A source publishing in another language would need it
 * extended — and a genuinely Chinese title containing one of these twenty codepoints WOULD be
 * damaged. Accepted: his sources are Turkish finance channels, and the alternative is 129 corrupt
 * strings and one analyst permanently split in two.
 *
 * ⚠ AND IT DELIBERATELY DOES NOT TOUCH `assetName` OR `_extId`. `_extId` is built as
 * `videoId + '::' + assetName`, so repairing the name after the fact would make the key disagree
 * with the one already in state — a NEW row on the next pull, which is the duplicate defect
 * `25e43c2` just closed. Zero live instances (no `assetName` is corrupt); recorded as the reason
 * the repair is scoped to display fields.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * mis-decoded codepoint → the codepoint it should have been, derived from UTF-8-read-as-GBK.
 *
 * ⚠ WRITTEN AS ESCAPES, FOR TWO REASONS. The `english-only` gate reads a Turkish letter in source
 * as a translation choice and fails the build — correctly, since it cannot tell copy from a
 * repair table, and `utils.js` is far too broad a file to hand a whole-file exemption to. And
 * after three raw control characters landed in source files today, a codepoint table that says
 * its codepoints out loud is the one form that cannot be corrupted by whatever wrote it.
 *
 * The test re-derives every pair with `TextDecoder('gbk')`, so the escapes are checked, not
 * trusted.
 */
export const MOJIBAKE_GBK = {
  '\u83BD': '\u00E7',   // c-cedilla
  '\u811F': '\u00C7',   // C-cedilla
  '\u81D2': '\u011F',   // g-breve
  '\u81D1': '\u011E',   // G-breve
  '\u8C0B': '\u0131',   // dotless-i
  '\u964C': '\u0130',   // I-with-dot
  '\u679A': '\u00F6',   // o-diaeresis
  '\u8130': '\u00D6',   // O-diaeresis
  '\u8267': '\u015F',   // s-cedilla
  '\u8266': '\u015E',   // S-cedilla
  '\u7709': '\u00FC',   // u-diaeresis
  '\u813A': '\u00DC',   // U-diaeresis
  '\u8292': '\u00E2',   // a-circumflex
  '\u8117': '\u00C2',   // A-circumflex
  '\u536F': '\u00EE',   // i-circumflex
  '\u8126': '\u00CE',   // I-circumflex
  '\u6CA1': '\u00FB',   // u-circumflex
  '\u8139': '\u00DB',   // U-circumflex
  '\u8305': '\u00E9',   // e-acute
  '\u8121': '\u00C9',   // E-acute
};

// ⚠ TWO REGEXES, AND THAT IS NOT AN OVERSIGHT. A `/g` regex carries `lastIndex` across `.test()`
// calls, so one shared instance would answer false on every other string it is asked about.
// `_MOJI_ANY` has no `/g` and is safe to test; `_MOJI_ALL` is only ever handed to `.replace`,
// which resets it itself.
const _MOJI_ANY = new RegExp('[' + Object.keys(MOJIBAKE_GBK).join('') + ']');
const _MOJI_ALL = new RegExp('[' + Object.keys(MOJIBAKE_GBK).join('') + ']', 'g');

/**
 * Repair a mis-decoded string, or hand back the **same string** untouched.
 *
 * ⚠ THE EARLY RETURN IS A PURE OPTIMISATION AND I FIRST WROTE THAT IT WAS NOT. The docblock
 * claimed the identical-return was "load-bearing", because `save()` compares with `!==` to decide
 * whether to stamp `_updatedAt`. **JavaScript compares string PRIMITIVES by value**, so a clean
 * string can never come back distinguishable however it is built — there is no version of this
 * function that breaks that, which is why two attempts to mutate it both survived. The guard that
 * actually keeps a clean book from stamping is the `if (hit)` in `save()`, and that one dies
 * instantly when mutated. Kept because it skips the replace machinery on ~150 strings every save;
 * labelled so nobody reads it as a correctness guarantee again. (rules #41 and #50)
 */
export function demojibake(s) {
  if (typeof s !== 'string' || !_MOJI_ANY.test(s)) return s;
  return s.replace(_MOJI_ALL, c => MOJIBAKE_GBK[c]);
}

export const PORTFOLIO_EXIT_REASONS = ['Exit Holding', 'AI Chat holding exit'];

export function realizedOrigin(r) {
  if (!r) return 'trade';
  if (isInterestRealized(r)) return 'interest';
  if (PORTFOLIO_EXIT_REASONS.includes(r.reason)) return 'portfolio';
  return 'trade';
}

/**
 * WHICH TRADE A REALIZED ROW BELONGS TO — one definition, four call sites, and a migration.
 *
 * `tradeId` is the link. It has always been optional, so four readers each grew their own
 * fallback *"…or any trade with the same ticker"*, and the four do not agree: `trades.js:1033`
 * filters on `status === 'Closed'`, `trades.js:1096` and `ai-trade-journal.js:40` do not, and
 * `trade-journal-page.js:48` filters but takes whichever `find` reaches first. Theme #1.
 *
 * ⚠ MEASURED 2026-08-17 on `backup/zagx-2026-08-16.json` (21 realized rows, 18 trades):
 *
 *     no tradeId, reason 'Exit Holding'   13   correct — a portfolio sale has no trade
 *     no tradeId, belongs to a trade       5   EREGL 04-13 · AKBNK 04-17 · GWIND 04-30
 *                                              TRYVD2 04-30 · XAU 05-06
 *     carries tradeId                      3
 *
 * All five resolve today, because each ticker has exactly one closed trade. **The ticker
 * fallback is not wrong yet; it is unopposed.** ⚠ `TRYVD2` has THREE trade records — one closed,
 * **two open** — and `confirmMaturity` / `closeDailyTd` still push their realized row with no
 * `tradeId` at all. So the next maturity gives the book two unlinked TRYVD2 rows and two closed
 * TRYVD2 trades, and the unfiltered fallbacks match every row to every trade: **each deposit's
 * journal entry would sum BOTH deposits' interest.** MINE, and the trigger is two open deposits
 * he already holds.
 *
 * So: the link is stamped at the two writers that forgot it, back-filled by migration v53 where
 * it can be PROVEN, and resolved here everywhere else. The fallback survives — a book restored
 * from an older export still needs it — but it must be **unambiguous**:
 *
 *   1. `tradeId` wins outright.
 *   2. A portfolio exit has no trade. Never guess one for it.
 *   3. Exactly one closed trade with that ticker → that one.
 *   4. Otherwise, exactly one whose close date is the row's date → that one.
 *   5. Otherwise **null**. A journal that says *"original trade record not found"* is worth more
 *      than one that attributes April's interest to May's deposit.
 */
export function tradeForRealized(r, trades) {
  if (!r) return null;
  const list = Array.isArray(trades) ? trades : [];
  if (r.tradeId) return list.find(t => t && t.id === r.tradeId) || null;
  if (realizedOrigin(r) === 'portfolio') return null;
  const closed = list.filter(t => t && t.ticker === r.ticker && t.status === 'Closed');
  if (closed.length === 1) return closed[0];
  if (!closed.length) return null;
  const day = String(r.date || '').slice(0, 10);
  if (!day) return null;
  const sameDay = closed.filter(t => String(t.closeDate || t.exitDate || '').slice(0, 10) === day);
  return sameDay.length === 1 ? sameDay[0] : null;
}

/** The inverse, through the same rule — so a row can never belong to two trades at once. */
export function realizedForTrade(t, realized, trades) {
  if (!t || !t.id) return [];
  return (Array.isArray(realized) ? realized : [])
    .filter(r => { const owner = tradeForRealized(r, trades); return !!owner && owner.id === t.id; });
}


/**
 * True when an open row is a DIRECTIONAL trade — something with a side, a market price and a
 * stop — as opposed to a time deposit, which has none of those and simply accrues.
 *
 * Why this exists. On 2026-08-03 the AI insight card said:
 *
 *   "All 10 open trades (DOAS, EKGYO, ALCAR, ZOREN, CEMTS, PGSUS, ARCLK, OYAKC + 2x TRYVD2)
 *    have sl=null. Unprotected TRY equity exposure risks amplified loss in a TRY selloff."
 *
 * TRYVD2 is a bank time deposit. It is not equity, it cannot have a stop-loss, and it cannot
 * gap down in a TRY selloff — its risk is rate-versus-inflation, which the same model wrote a
 * separate and correct insight about two cards below. Three prompt builders filtered only on
 * `status !== 'Closed'`, so deposits arrived labelled as open trades with `sl: null`, and the
 * model did the only reasonable thing with that input: it told the owner to set stops on his
 * savings, and it inflated the trade count from 8 to 10 while doing it.
 *
 * The model was not hallucinating. It was reading a category error we handed it. Fix the
 * category, not the prompt. (audit 2026-08-03)
 */
export function isDirectionalTrade(t) {
  return !!t && t.type !== 'TimeDeposit';
}

/** Open directional trades — the set that SL/TP/side reasoning is valid for. */
export function openTrades(trades) {
  return (trades || []).filter(t => t && !t._del && t.status !== 'Closed' && isDirectionalTrade(t));
}

/**
 * Date string formatter (en-GB format)
 */
export const ds = iso =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * A-1: Canonical cash sum by currency from a cashTx ledger.
 * Single source of truth for reconstructing balances — `_audit` (income-tracking)
 * entries are excluded by default, matching the real cash pool. Use this everywhere
 * a balance is derived from cashTxs so the F-1 filter bug cannot recur.
 * @returns {{USD:number,TRY:number,EUR:number}}
 */
export function cashSumByCurrency(cashTxs, { excludeAudit = true } = {}) {
  const out = { USD: 0, TRY: 0, EUR: 0 };
  (cashTxs || []).forEach(tx => {
    if (excludeAudit && tx._audit) return;
    const cur = tx.currency || 'USD';
    out[cur] = (out[cur] || 0) + (tx.amount || 0);
  });
  return out;
}

/**
 * Month label formatter (e.g., "Apr-2024")
 */
export const ml = m => {
  const [y, mo] = m.split('-');
  return (
    ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+mo] +
    '-' +
    y
  );
};

/**
 * Is this string readable on an English-only surface?
 *
 * SPEKTRX ships in English. News evidence quotes arrive in whatever language the source wrote
 * them, so the impact pipeline carries `evidenceQuote` (verbatim, what anti-hallucination
 * validation runs against) and `evidenceQuoteEn` (the display copy). Nothing checked that the
 * display copy was actually English.
 *
 * 2026-08-08, on a live scan: the source headline was ENGLISH — "CVX Stock Has A New Bull Case
 * — Why Bank Of America Favors Chevron…" — and the model returned its Mandarin translation as
 * `evidence_quote_en`. It was stored, and the Advisory page rendered Chinese to an owner who
 * does not read it. The translation pass could not save it either: its trigger is "same as the
 * original AND non-ASCII", and this was neither.
 *
 * Script detection, not language detection. Turkish, French and German are Latin and pass —
 * they are handled by the non-ASCII translation trigger. This catches the case where the text
 * is not even in an alphabet the owner can read, which is never a display copy whatever it says.
 */
export function isLatinReadable(t) {
  const s = String(t || '').trim();
  if (!s) return false;
  // Cyrillic, Hebrew, Arabic, Devanagari, Thai, Kana, CJK, Hangul
  return !/[\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0900-\u097F\u0E00-\u0E7F\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uAC00-\uD7AF]/.test(s);
}
